import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import EMPAccordion from "../../../shared/components/EMPAccordion/EMPAccordion";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import { UserContext } from "../../../shared/context/userContext";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  TRADE_IN_VALUE_ESTIMATED,
  TRADE_IN_VALUE_LINCOLN_BACKED,
  mapProgressTradeInToRadioSelection,
} from "../../../shared/utils/onboardingProgressUtil";
import styles from "./OnboardingTradeIn.module.scss";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import accordionStyles from "../../../shared/components/EMPAccordion/EMPAccordionLincoln.module.scss";

const ACCESS_MANAGEMENT_URL = "https://www.accessmgmt.ford.com/aga-home";

const entitlementPeople = [
  {
    name: "Russell Westbrook",
    role: "Dealer/Partner",
    phone: "123-456-7890",
  },
  {
    name: "Kobe Bryant",
    role: "Dealer/Partner",
    phone: "123-456-7890",
  },
  {
    name: "Lebron James",
    role: "Dealer/Partner",
    phone: "123-456-7890",
  },
];

const renderChevronCircle = () => (
  <span className={styles.chevronCircle} aria-hidden="true">
    <span className={styles.chevronIcon} />
  </span>
);

const renderButtonContent = (label) => (
  <span className={styles.pdfButtonContent}>
    <span className={styles.pdfButtonLabel}>{label}</span>
    {renderChevronCircle()}
  </span>
);

const TradeInPreferenceAccordion = ({
  t,
  expanded,
  onChange,
  tradeInPreference,
  onTradeInPreferenceChange,
  onSaveTradeInPreferenceClick,
  isSaved,
  isSaving,
  hasSavedSelection,
  showConfirmation,
  onCloseConfirmation,
  saveError,
  onCloseError,
}) => {
  const radioIcon = <span className={styles.radioIcon} />;
  const checkedRadioIcon = (
    <span className={`${styles.radioIcon} ${styles.radioIconChecked}`} />
  );

  const isSaveTradeInPreferenceDisabled =
    !tradeInPreference || isSaving || isSaved;

  const saveButtonLabel = isSaved
    ? t("DealerOnboarding.content.tradeIn.tradeInPreference.saved", "Saved")
    : t("DealerOnboarding.content.tradeIn.tradeInPreference.save", "Save");

  return (
    <EMPAccordion
      heading={t(
        "DealerOnboarding.content.tradeIn.tradeInPreference.title",
        "Trade-In Preference"
      )}
      headingClassName={accordionStyles.accordionHeading}
      expanded={expanded}
      onChange={onChange}
      prefixIconType={hasSavedSelection ? "check" : "caution"}
      toggleIconColor="grey"
    >
      <Box
        className={styles.tradeInPreferenceContent}
        data-testid="trade-in-preference-content"
      >
        {showConfirmation && (
          <EMPConfirmationBanner
            variant="success"
            primaryText={t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.stepComplete",
              "Step is complete"
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.closeConfirmationBanner",
              "Close confirmation banner"
            )}
            onClose={onCloseConfirmation}
            testId="trade-in-preference-completion-success"
          />
        )}

        {saveError && (
          <EMPConfirmationBanner
            variant="error"
            primaryText={t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.saveError",
              "Could not save preference. Please try again."
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.closeErrorBanner",
              "Close error banner"
            )}
            onClose={onCloseError}
            testId="trade-in-preference-save-error"
          />
        )}

        <Typography variant="body1" className={styles.copy}>
          {t(
            "DealerOnboarding.content.tradeIn.tradeInPreference.description",
            "Configure how your dealership handles online trade-in submissions from Lincoln.com Digital Retail guests. While you have flexibility, we highly recommend selecting the Lincoln-Backed Value to streamline your sales process, eliminate inventory risk, and build immediate trust with your buyers."
          )}
        </Typography>

        <Box className={styles.optionSection}>
          <Typography component="h3" className={styles.optionHeading}>
            {t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionHeading",
              "Option 1: Lincoln-Backed Value (Recommended)"
            )}
          </Typography>

          <Typography variant="body1" className={styles.copy}>
            {t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.descriptionOne",
              "Choosing the Lincoln-Backed Value provides your guests with a firm, guaranteed appraisal while giving your dealership ultimate flexibility and zero financial risk."
            )}
          </Typography>

          <Typography variant="body1" className={styles.copy}>
            {t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.descriptionTwo",
              "When a guest submits their vehicle with this option, you have three ways to handle the transaction:"
            )}
          </Typography>

          <ol className={styles.lincolnBackedList}>
            <li>
              <strong>
                {t(
                  "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionOne.label",
                  "Retain for Inventory:"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionOne.description",
                "Keep the vehicle for your own pre-owned inventory and purchase it at the guaranteed price."
              )}
            </li>
            <li>
              <strong>
                {t(
                  "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionTwo.label",
                  "Redeem via ARS (Zero Risk):"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionTwo.description",
                "If you do not want the vehicle for your lot, send it to auction through Automotive Remarketing Services (ARS) and fully redeem the backed value."
              )}
            </li>
            <li>
              <strong>
                {t(
                  "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionThree.label",
                  "Reject (Condition Match):"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.optionThree.description",
                "If the vehicle’s physical condition does not match the online appraisal details provided by the guest, you can reject the backed value and easily transition the deal to an offline, dealer-negotiated trade."
              )}
            </li>
          </ol>
        </Box>

        <Box className={styles.optionSection}>
          <Typography component="h3" className={styles.optionHeading}>
            {t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.estimatedValue.optionHeading",
              "Option 2: Estimated Value"
            )}
          </Typography>

          <Typography variant="body1" className={styles.copy}>
            {t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.estimatedValue.description",
              "If you opt out of the Lincoln-backed tool, guests will only receive a broad estimation range online. Your team will be responsible for manually evaluating the vehicle, managing offline negotiations, and establishing a final trade-in price with the guest to finalize the transaction."
            )}
          </Typography>
        </Box>

        <FormControl component="fieldset" className={styles.radioForm}>
          <Typography component="p" className={styles.radioQuestion}>
            {t(
              "DealerOnboarding.content.tradeIn.tradeInPreference.question",
              "Which option would you like?"
            )}
          </Typography>

          <RadioGroup
            name="trade-in-preference"
            value={tradeInPreference}
            onChange={onTradeInPreferenceChange}
            className={styles.radioGroup}
            data-testid="trade-in-preference-radio-group"
          >
            <FormControlLabel
              value={TRADE_IN_VALUE_LINCOLN_BACKED}
              control={
                <Radio
                  className={styles.radioButton}
                  icon={radioIcon}
                  checkedIcon={checkedRadioIcon}
                  disableRipple
                />
              }
              label={t(
                "DealerOnboarding.content.tradeIn.tradeInPreference.lincolnBackedValue.label",
                "Lincoln-Backed Value"
              )}
              className={styles.radioOption}
              data-testid="lincoln-backed-value-radio-option"
            />

            <FormControlLabel
              value={TRADE_IN_VALUE_ESTIMATED}
              control={
                <Radio
                  className={styles.radioButton}
                  icon={radioIcon}
                  checkedIcon={checkedRadioIcon}
                  disableRipple
                />
              }
              label={t(
                "DealerOnboarding.content.tradeIn.tradeInPreference.estimatedValue.label",
                "Estimated Value"
              )}
              className={styles.radioOption}
              data-testid="estimated-value-radio-option"
            />
          </RadioGroup>
        </FormControl>

        <Box className={styles.saveButtonContainer}>
          <EMPButton
            variant="contained"
            onClick={onSaveTradeInPreferenceClick}
            disabled={isSaveTradeInPreferenceDisabled}
            testId="trade-in-preference-save-button"
            className={styles.saveButton}
          >
            {saveButtonLabel}
          </EMPButton>
        </Box>
      </Box>
    </EMPAccordion>
  );
};

TradeInPreferenceAccordion.propTypes = {
  t: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  tradeInPreference: PropTypes.string.isRequired,
  onTradeInPreferenceChange: PropTypes.func.isRequired,
  onSaveTradeInPreferenceClick: PropTypes.func.isRequired,
  isSaved: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  hasSavedSelection: PropTypes.bool.isRequired,
  showConfirmation: PropTypes.bool.isRequired,
  onCloseConfirmation: PropTypes.func.isRequired,
  saveError: PropTypes.bool.isRequired,
  onCloseError: PropTypes.func.isRequired,
};

const ArsAccordion = ({ t, expanded, onChange, onPdfButtonClick }) => {
  return (
    <EMPAccordion
      heading={t(
        "DealerOnboarding.content.tradeIn.ars.title",
        "Automotive Remarketing Services (ARS)"
      )}
      headingClassName={accordionStyles.accordionHeading}
      expanded={expanded}
      onChange={onChange}
      prefixIconType="caution"
      toggleIconColor="grey"
    >
      <Box className={styles.arsContent} data-testid="ars-content">
        <Box className={styles.arsCopyContainer}>
          <Typography variant="body1" className={styles.copy}>
            {t(
              "DealerOnboarding.content.tradeIn.ars.descriptionParagraphOne",
              "Lincoln has built a tool that allows a customer to receive a Lincoln-backed value with their trade-in vehicle submission. Dealers maintain full control and make decisions about accepting the vehicle to retain for inventory, sending the vehicle to auction through Automotive Remarketing Services (ARS) to redeem the Lincoln-backed value, or rejecting the vehicle. Dealers must be enrolled in the ARS program to redeem Lincoln offers."
            )}
          </Typography>

          <Typography variant="body1" className={styles.copy}>
            {t(
              "DealerOnboarding.content.tradeIn.ars.descriptionParagraphTwo",
              "To enroll in ARS, download and complete the following three forms and email them directly to ARS@ford.com with the subject line: <your dealership name> - ARS Enrollment Docs"
            )}
          </Typography>

          <Typography variant="body1" className={styles.copy}>
            {t(
              "DealerOnboarding.content.tradeIn.ars.descriptionParagraphThree",
              "Enrollment can take up to 10 business days to complete."
            )}
          </Typography>
        </Box>

        <Box
          className={styles.pdfButtonContainer}
          data-testid="ars-pdf-buttons"
        >
          <EMPButton
            variant="outlined"
            onClick={onPdfButtonClick}
            testId="ars-dealer-information-form-button"
            className={styles.pdfButton}
          >
            {renderButtonContent(
              t(
                "DealerOnboarding.content.tradeIn.ars.dealerInformationForm",
                "ARS Dealer Information Form"
              )
            )}
          </EMPButton>

          <EMPButton
            variant="outlined"
            onClick={onPdfButtonClick}
            testId="ars-dealer-code-payment-button"
            className={styles.pdfButton}
          >
            {renderButtonContent(
              t(
                "DealerOnboarding.content.tradeIn.ars.dealerCodePayment",
                "ARS Dealer Code Payment"
              )
            )}
          </EMPButton>

          <EMPButton
            variant="outlined"
            onClick={onPdfButtonClick}
            testId="ars-remarketing-agreement-button"
            className={styles.pdfButton}
          >
            {renderButtonContent(
              t(
                "DealerOnboarding.content.tradeIn.ars.remarketingAgreement",
                "ARS Remarketing Agreement"
              )
            )}
          </EMPButton>
        </Box>
      </Box>
    </EMPAccordion>
  );
};

ArsAccordion.propTypes = {
  t: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onPdfButtonClick: PropTypes.func.isRequired,
};

const EntitlementsAccordion = ({ t, expanded, onChange }) => {
  return (
    <EMPAccordion
      heading={t(
        "DealerOnboarding.content.tradeIn.entitlements.title",
        "Entitlement(s)"
      )}
      headingClassName={accordionStyles.accordionHeading}
      expanded={expanded}
      onChange={onChange}
      prefixIconType="caution"
      toggleIconColor="grey"
    >
      <Box
        className={styles.entitlementsContent}
        data-testid="entitlements-content"
      >
        <Typography variant="body1" className={styles.copy}>
          {t(
            "DealerOnboarding.content.tradeIn.entitlements.descriptionPartOne",
            "Setting entitlements must be done before you can enroll vehicles in ARS. Previously assigned personnel and entitlements are shown on this screen. Select"
          )}{" "}
          <strong>
            {t(
              "DealerOnboarding.content.tradeIn.entitlements.setEntitlementsInline",
              "Set Entitlements"
            )}
          </strong>{" "}
          {t(
            "DealerOnboarding.content.tradeIn.entitlements.descriptionPartTwo",
            "to make changes. Only authorized dealer users should be granted trade-in module entitlements."
          )}
        </Typography>

        <Box
          className={styles.entitlementsNeededRow}
          data-testid="entitlements-needed-row"
        >
          <Typography variant="body1" className={styles.copy}>
            <strong>
              {t(
                "DealerOnboarding.content.tradeIn.entitlements.toBeSetLabel",
                "Entitlements to be set:"
              )}
            </strong>{" "}
            {t(
              "DealerOnboarding.content.tradeIn.entitlements.toBeSetValue",
              "ARSInvoice and ARSVehicleNotification"
            )}
          </Typography>

          <span className={styles.completedPill} data-testid="completed-pill">
            {t(
              "DealerOnboarding.content.tradeIn.entitlements.completed",
              "Completed"
            )}
          </span>
        </Box>

        <Box className={styles.peopleSection}>
          <Typography variant="body1" className={styles.peopleSectionTitle}>
            {t(
              "DealerOnboarding.content.tradeIn.entitlements.peopleWithEntitlements",
              "People with the ARSInvoice and ARVehicleNotification Entitlements:"
            )}
          </Typography>

          <Box
            className={styles.peopleGrid}
            data-testid="entitlement-people-grid"
          >
            {entitlementPeople.map((person) => (
              <Box
                key={person.name}
                className={styles.personCard}
                data-testid="entitlement-person-card"
              >
                <Typography variant="body1" className={styles.personName}>
                  {person.name}
                </Typography>

                <Typography variant="body1" className={styles.personDetails}>
                  {person.role}, {person.phone}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={styles.entitlementsButtonContainer}>
          <a
            href={ACCESS_MANAGEMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.linkoutButton}
            data-testid="set-entitlements-link"
          >
            {renderButtonContent(
              t(
                "DealerOnboarding.content.tradeIn.entitlements.setEntitlements",
                "Set Entitlements"
              )
            )}
          </a>
        </Box>
      </Box>
    </EMPAccordion>
  );
};

EntitlementsAccordion.propTypes = {
  t: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const OnboardingTradeIn = ({ savedTradeInPreference }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;
  const userId = user?.userId || user?.commonName;

  const [expandedTradeInPreference, setExpandedTradeInPreference] =
    useState(false);
  const [expandedArs, setExpandedArs] = useState(false);
  const [expandedEntitlements, setExpandedEntitlements] = useState(false);
  const [tradeInPreference, setTradeInPreference] = useState(() =>
    mapProgressTradeInToRadioSelection(savedTradeInPreference)
  );
  const [savedApiSelection, setSavedApiSelection] = useState(() =>
    mapProgressTradeInToRadioSelection(savedTradeInPreference)
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    const mappedSelection = mapProgressTradeInToRadioSelection(
      savedTradeInPreference
    );
    setTradeInPreference(mappedSelection);
    setSavedApiSelection(mappedSelection);
  }, [savedTradeInPreference]);

  const saveTradeInPreferenceMutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.saveTradeInPreference(dealerCommonId, payload),
    onSuccess: async (_data, variables) => {
      setSaveError(false);
      await queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
        exact: true,
        refetchType: "active",
      });
      setSavedApiSelection(variables.tradeInPreference);
      setShowConfirmation(true);
    },
    onError: () => {
      setShowConfirmation(false);
      setSaveError(true);
    },
  });

  const handleTradeInPreferenceAccordionChange = () => {
    setExpandedTradeInPreference(!expandedTradeInPreference);
  };

  const handleArsAccordionChange = () => {
    setExpandedArs(!expandedArs);
  };

  const handleEntitlementsAccordionChange = () => {
    setExpandedEntitlements(!expandedEntitlements);
  };

  const handleTradeInPreferenceChange = (event) => {
    setTradeInPreference(event.target.value);
    setShowConfirmation(false);
    setSaveError(false);
  };

  const isSaved =
    Boolean(tradeInPreference) && tradeInPreference === savedApiSelection;

  const hasSavedSelection = Boolean(savedApiSelection);

  const handleSaveTradeInPreferenceClick = () => {
    if (
      !tradeInPreference ||
      !dealerCommonId ||
      saveTradeInPreferenceMutation.isPending ||
      isSaved
    ) {
      if (!dealerCommonId) {
        setShowConfirmation(false);
        setSaveError(true);
      }
      return;
    }

    setShowConfirmation(false);
    setSaveError(false);
    saveTradeInPreferenceMutation.mutate({
      tradeInPreference,
      updatedBy: userId,
    });
  };

  const handlePdfButtonClick = () => {
    // Dummy PDF buttons for this story.
    // Download functionality will be added in a future story if needed.
  };

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle testId="trade-in-title" className="tw-mb-6">
        {t("DealerOnboarding.content.tradeIn.title", "Trade-In")}
      </OnboardingPageTitle>

      <div className={styles.accordionList} data-testid="onboarding-trade-in">
        <TradeInPreferenceAccordion
          t={t}
          expanded={expandedTradeInPreference}
          onChange={handleTradeInPreferenceAccordionChange}
          tradeInPreference={tradeInPreference}
          onTradeInPreferenceChange={handleTradeInPreferenceChange}
          onSaveTradeInPreferenceClick={handleSaveTradeInPreferenceClick}
          isSaved={isSaved}
          isSaving={saveTradeInPreferenceMutation.isPending}
          hasSavedSelection={hasSavedSelection}
          showConfirmation={showConfirmation}
          onCloseConfirmation={() => setShowConfirmation(false)}
          saveError={saveError}
          onCloseError={() => setSaveError(false)}
        />

        <ArsAccordion
          t={t}
          expanded={expandedArs}
          onChange={handleArsAccordionChange}
          onPdfButtonClick={handlePdfButtonClick}
        />

        <EntitlementsAccordion
          t={t}
          expanded={expandedEntitlements}
          onChange={handleEntitlementsAccordionChange}
        />
      </div>
    </div>
  );
};

OnboardingTradeIn.propTypes = {
  savedTradeInPreference: PropTypes.string,
};

OnboardingTradeIn.defaultProps = {
  savedTradeInPreference: "",
};

export default OnboardingTradeIn;
